import { useQuery } from '@tanstack/react-query';

/**
 * Public client-dashboard API (no auth — the share token in the URL is the
 * credential). Mirrors the backend's ClientShareView allowlist: no money, no
 * problem reports, no reporter identities.
 */

export interface ShareTrackItem {
  id: string;
  name: string;
  sequence: number;
  totalUnits: number;
  currentUnits: number;
  /** Sub-task progress, 0–100. */
  progressPct: number;
  status: string;
  plannedStart: string | null;
  plannedEnd: string | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  baselineStart: string | null;
  baselineEnd: string | null;
}

export interface ShareDependency {
  id: string;
  predecessorId: string;
  successorId: string;
  lagDays: number;
}

/** Polymorphic item↔milestone schedule edge — drives the dashed milestone arrows. */
export interface ShareMilestoneLink {
  id: string;
  predecessorKind: 'item' | 'milestone';
  predecessorId: string;
  successorKind: 'item' | 'milestone';
  successorId: string;
  lagDays: number;
}

export interface ShareTimeline {
  criticalPath: string[];
  holidays: string[];
  projectFinish: string | null;
}

export interface ShareTrack {
  id: string;
  type: string;
  label: string;
  unitLabel: string;
  totalUnits: number;
  currentUnits: number;
  /** Weighted-by-size roll-up of the sub-tasks, or the track's own counter when it has none. */
  progressPct: number;
  status: string;
  dueDate: string | null;
  plannedStart: string | null;
  plannedEnd: string | null;
  items: ShareTrackItem[];
}

export interface ShareMilestone {
  id: string;
  name: string;
  dueDate: string;
  /** Engine-computed date ("graph wins"); the timeline places the diamond at
   *  scheduledDate ?? dueDate, matching the internal hub. */
  scheduledDate: string | null;
  completedDate: string | null;
  status: string;
  delayReason: string | null;
}

export interface ShareUpdate {
  id: string;
  trackId: string;
  reportedDate: string;
  progressIncrement: number;
  isBlocked: boolean;
  note: string | null;
  photos: Array<{ id: string; caption: string | null }>;
}

/** One server-paginated page of the updates feed. */
export interface UpdatesPage {
  items: ShareUpdate[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ShareFile {
  id: string;
  originalName: string;
  category: string;
  description: string | null;
  sizeBytes: number;
  mimeType: string;
  uploadedAt: string;
}

export interface ClientShareView {
  project: {
    code: string;
    name: string;
    description: string | null;
    status: string;
    startDate: string | null;
    expectedEndDate: string | null;
    actualEndDate: string | null;
  };
  clientName: string | null;
  overallPct: number;
  tracks: ShareTrack[];
  dependencies: ShareDependency[];
  milestoneLinks: ShareMilestoneLink[];
  timeline: ShareTimeline;
  deliveryMilestones: ShareMilestone[];
  updates: ShareUpdate[];
  files: ShareFile[];
}

export async function fetchClientShareView(token: string): Promise<ClientShareView> {
  const res = await fetch(`/api/public/shares/${encodeURIComponent(token)}`, {
    headers: { Accept: 'application/json' },
  });
  if (res.status === 404) throw new Error('SHARE_NOT_FOUND');
  if (!res.ok) throw new Error(`Failed to load (${res.status})`);
  return res.json() as Promise<ClientShareView>;
}

/** Fetch one page of the updates feed for a public share link (newest first). */
export async function fetchShareUpdatesPage(
  token: string,
  page: number,
  pageSize: number,
): Promise<UpdatesPage> {
  const res = await fetch(
    `/api/public/shares/${encodeURIComponent(token)}/updates?page=${page}&pageSize=${pageSize}`,
    { headers: { Accept: 'application/json' } },
  );
  if (!res.ok) throw new Error(`Failed to load updates (${res.status})`);
  return res.json() as Promise<UpdatesPage>;
}

export function sharePhotoUrl(token: string, photoId: string): string {
  return `/api/public/shares/${encodeURIComponent(token)}/photos/${photoId}`;
}

export function shareFileUrl(token: string, fileId: string): string {
  return `/api/public/shares/${encodeURIComponent(token)}/files/${fileId}`;
}

export function useClientShareView(token: string | undefined) {
  return useQuery({
    queryKey: ['public-share', token],
    queryFn: () => fetchClientShareView(token!),
    enabled: Boolean(token),
    retry: (count, err) => (err as Error).message !== 'SHARE_NOT_FOUND' && count < 2,
  });
}
